import { CbhIcon, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { formatDistanceInMiles } from "@src/appV2/lib/utils/distance";
import { jobTypeOptions, shiftTypeOptions } from "@src/appV2/redesign/PlacementCandidate/constants";

import { type PlacementData } from "../../../types/placement";

interface PlacementCardBodyProps {
  placementId: string;
  blurPlacement: boolean;
  jobShifts: PlacementData["jobShifts"];
  workplaceName: string;
  workplaceNeighborhood?: string;
  distance?: number;
}

function SlashSeparatedList(props: { children: JSX.Element[] }) {
  const { children } = props;

  return (
    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
      {children.reduce<React.ReactNode[]>((accumulator, element, index) => {
        if (index === 0) {
          return [element];
        }

        return [
          ...accumulator,
          <Text key={`separator-${element?.key}`} variant="body2">
            /
          </Text>,
          element,
        ];
      }, [])}
    </Stack>
  );
}

export function PlacementCardBody(props: PlacementCardBodyProps) {
  const { placementId, blurPlacement, jobShifts, workplaceNeighborhood, workplaceName, distance } =
    props;

  return (
    <Stack spacing={2}>
      <SlashSeparatedList>
        {jobShifts
          .map((jobShift) => {
            const { jobType, shiftTypes, daysOfWeek } = jobShift;

            const label = jobTypeOptions.find((option) => option.value === jobType)?.label;
            if (!label) {
              return null;
            }

            return (
              <Stack
                key={`label-${placementId}-${jobType}-${daysOfWeek.join("-")}-${shiftTypes.join(
                  "-"
                )}`}
                direction="row"
                gap="2.5px"
                alignItems="center"
                component="span"
              >
                <Text variant="body2" sx={{ fontWeight: 500 }}>
                  {label}
                </Text>
                <Stack direction="row" alignItems="center" component="span" spacing="2px">
                  {shiftTypes?.map((shiftType) => {
                    const shiftTypeOption = shiftTypeOptions.find(
                      (option) => option.value === shiftType
                    );
                    return (
                      <CbhIcon
                        key={`icon-${placementId}-${shiftType}`}
                        type={shiftTypeOption?.iconOutlined ?? "sun-outlined"}
                        sx={{ width: 14, height: 14 }}
                      />
                    );
                  })}
                </Stack>
              </Stack>
            );
          })
          .filter(isDefined)}
      </SlashSeparatedList>
      <Text
        variant="body2"
        color={(theme) => theme.palette.text.secondary}
        sx={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          filter: blurPlacement ? "blur(8px)" : undefined,
        }}
      >
        {workplaceName}
      </Text>
      <Stack direction="row" alignItems="center" spacing={1}>
        {isDefined(workplaceNeighborhood) && (
          <Text
            variant="body2"
            color={(theme) => theme.palette.text.tertiary}
            sx={{ filter: blurPlacement ? "blur(8px)" : undefined }}
          >
            {workplaceNeighborhood}
          </Text>
        )}
        {isDefined(distance) && isDefined(workplaceNeighborhood) && (
          <CbhIcon
            type="dot"
            size="xSmall"
            sx={{
              color: (theme) => theme.palette.text.tertiary,
              filter: blurPlacement ? "blur(8px)" : undefined,
            }}
          />
        )}
        {isDefined(distance) && (
          <Text
            variant="body2"
            color={(theme) => theme.palette.text.tertiary}
            sx={{ filter: blurPlacement ? "blur(8px)" : undefined }}
          >
            {formatDistanceInMiles(distance)}
          </Text>
        )}
      </Stack>
    </Stack>
  );
}
